import React, { useState } from "react";
import { Keyboard, View } from "react-native";
import EmployeeUtil from "utils/employee-util";
import i18n from "utils/i18n";
import { useSelector } from "react-redux";
import { dataConstants } from "services/redux/data/constants";
import * as ImagePicker from "expo-image-picker";
import _ from "lodash";
import { layoutStyles } from "styles/layout";
import Form from "components/molecules/Form";
import { FormFieldsConstants } from 'utils/constants/form-constants';
import { ThemedSizes } from "styles/themed";
import ThemedBlock from "components/atoms/ThemedBlock";
import HelperSetting from "utils/helper-setting";
import { settingsConstants } from "utils/constants/settings-constants";
import Text from "theme/Text";
import HelperPlatform from "utils/helper-platform";
import * as ImageManipulator from 'expo-image-manipulator';
import EmployeeCard, { EmployeeCardModes } from "components/molecules/EmployeeCard";
import EmployeeValues from 'scenes/home/more/_functions/Employee'
import HelperSelect from 'utils/HelperSelect'
import store from 'services/redux/store'
import HelperAlerts from 'utils/HelperAlerts'
import { TESTID } from '_shared/config/testID'
import { ProfileStyles } from 'scenes/home/more/profile/styles/ProfileStyles'
import ThemedPressable from 'components/atoms/ThemedPressable'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import VStack from 'components/atoms/stack/VStack'
import HelperEmployee from 'utils/HelperEmployee'

const ProfileTab = ({ setError, onUpdate }) => {
	
	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} );
	const _teams = useSelector( state => {
		return state.data[dataConstants.TEAMS];
	} );
	const _departments = useSelector( state => {
		return state.data[dataConstants.DEPARTMENTS];
	} );
	const _jobtitles = useSelector( state => {
		return state.data[dataConstants.JOBTITLES];
	} );
	const _lunchOptions = store.getState().data[ dataConstants.LUNCH_OPTIONS ];
	
	const [ uploadingImage, setUploadingImage ] = useState( false );
	
	const department = EmployeeValues.getEmployeeField( _employee, 'department', null );
	const jobTitle = EmployeeValues.getEmployeeField( _employee, 'jobtitle', null );
	const team = EmployeeValues.getEmployeeField( _employee, 'team', null );
	const lunchOption = EmployeeValues.getEmployeeField( _employee, 'lunch_option', null );

	const data = {
		name: EmployeeValues.getEmployeeField( _employee, 'name' ),
		phonenumber: EmployeeValues.getEmployeeField( _employee, 'phonenumber' ),
		landline_phonenumber: EmployeeValues.getEmployeeField( _employee, 'landline_phonenumber' ),
		access_card_id: EmployeeValues.getEmployeeField( _employee, 'access_card_id' ),
		jobtitle_guid: jobTitle?.guid,
		department_guid: department?.guid,
		team_guid: team?.guid,
		lunch_option_guid: _.get( lunchOption, 'guid', null )
	}
	
	// get the jobtitles
	const jobtitleOptions = HelperSelect.createKeyValue({
		input: _jobtitles,
		omit_non_active_except_guid: _.get( _employee, ['jobtitle', 'guid'], null )
	});
	
	const departmentOptions = HelperSelect.createKeyValue({
		input: _departments,
		omit_non_active_except_guid: _.get( _employee, ['department', 'guid'], null )
	});
	
	const formFields = [];
	
	if( HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_NAME_EDITING ) || HelperSetting.isYes( settingsConstants.AD_SYNC_ENABLED ) ){
		formFields.push(
			{
				name: 'name',
				label: i18n.t('form.fields.name.title'),
				type: FormFieldsConstants.TEXT,
				disabled: ( HelperSetting.isYes( settingsConstants.AD_SYNC_ENABLED ) ),
				props: { returnKeyType: 'done', onSubmitEditing: Keyboard.dismiss, testID: TESTID.HOME.MORE.PROFILE.NAME_INPUT }
			}
		)
	}
	
	if( HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_PHONENUMBER_EDITING ) ){
		formFields.push(
			{
				name: 'phonenumber',
				label: i18n.t('form.fields.phonenumber.title'),
				type: FormFieldsConstants.TEXT,
				props: { returnKeyType: 'done', onSubmitEditing: Keyboard.dismiss, testID: TESTID.HOME.MORE.PROFILE.PHONENUMBER_INPUT }
			}
		)
	}
	
	if( HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_LANDLINE_PHONENUMBER_EDITING ) ){
		formFields.push(
			{
				name: 'landline_phonenumber',
				label: i18n.t('form.fields.landline_phonenumber.title'),
				type: FormFieldsConstants.TEXT,
				props: { returnKeyType: 'done', onSubmitEditing: Keyboard.dismiss, testID: TESTID.HOME.MORE.PROFILE.LANDLINE_PHONENUMBER_INPUT }
			}
		)
	}
	
	// Only when there are lunch options
	let lunchOptions = HelperSelect.createKeyValue({
		input: _lunchOptions,
		omit_non_active_except_guid: _.get( _employee, ['lunch_option', 'guid'], null )
	});
	
	if( _.size( lunchOptions ) > 0 ) {
		lunchOptions.unshift( { value: null, label: i18n.t('pages.more.profile.no_preference') } );
		
		formFields.push({
			name: 'lunch_option_guid',
			label: i18n.t('form.fields.lunch_option.title'),
			hint: i18n.t('form.fields.lunch_option.description'),
			type: FormFieldsConstants.SELECT,
			options: lunchOptions
		});
	}
	
	if( HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_ACCESS_CARD_ID_EDITING ) ){
		formFields.push({
			name: 'access_card_id',
			label: i18n.t('form.fields.access_card_id.title'),
			hint: i18n.t('form.fields.access_card_id.description'),
			type: FormFieldsConstants.TEXT,
			props: { returnKeyType: 'done', onSubmitEditing: Keyboard.dismiss, testID: TESTID.HOME.MORE.PROFILE.ACCESS_CARD_ID_INPUT }
		});
	}
	
	if( formFields.length > 0 ){
		formFields.push({ type: FormFieldsConstants.DIVIDER });
	}
	
	if( _.size( jobtitleOptions ) > 0 ){
		formFields.push({
			name: 'jobtitle_guid',
			label: i18n.t('form.fields.jobtitle.title'),
			type: FormFieldsConstants.SELECT,
			options: jobtitleOptions,
			disabled: HelperSetting.isNo( settingsConstants.PROFILE_ENABLE_JOBTITLE_EDITING )
		});
	}
	
	if( _.size( departmentOptions ) > 0 ){
		formFields.push({
			name: 'department_guid',
			label: i18n.t('form.fields.department.title'),
			type: FormFieldsConstants.SELECT,
			options: departmentOptions,
			disabled: HelperSetting.isNo( settingsConstants.PROFILE_ENABLE_DEPARTMENT_EDITING )
		});
	}
	
	if( HelperSetting.isYes( settingsConstants.ENABLE_TEAMS ) ) {
		let team_options = HelperSelect.createKeyValue({
			input: _teams,
			omit_non_active_except_guid: _.get( _employee, ['team', 'guid'], null )
		});
		
		if( _.size( team_options ) > 0 ){
			formFields.push({
				name: 'team_guid',
				label: i18n.t('form.fields.team.title'),
				type: FormFieldsConstants.SELECT,
				options: team_options,
				disabled: HelperSetting.isNo( settingsConstants.PROFILE_ENABLE_TEAM_EDITING )
			});
		}
	}
	
	const onChange = ( data ) => {
		EmployeeValues.onChangeEmployee( data );
		onUpdate( data )
	}
	
	/**
	 *
	 * @returns {Promise<void>}
	 */
	const getPermissionAsync = async ( ) => {
		if ( HelperPlatform.isWeb() ) {
			await pickImage();
			return;
		}
		
		const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
		
		if (status !== 'granted') {
			HelperAlerts.alert(
				i18n.t('page.profile.profile.no_roll_permission')
			);
		} else {
			await pickImage();
		}
	};
	
	/**
	 *
	 * @returns {Promise<void>}
	 */
	const pickImage = async () => {
		setUploadingImage( true );
		
		const request = async () => {
			try {
				const result = await ImagePicker.launchImageLibraryAsync( {
					mediaTypes: ImagePicker.MediaTypeOptions.Images,
					allowsEditing: true,
					aspect: [
						1,
						1
					],
					quality: 1, //base64: true,
					exif: false
				} );
				
				if ( !result.cancelled ) {
					// Resize the image
					const manipResult = await ImageManipulator.manipulateAsync( result.uri, [
						{
							resize: {
								width: 1000
							}
						}
					], {
						compress: 0.8,
						format: ImageManipulator.SaveFormat.JPEG,
						base64: true
					} );
					
					uploadImage( manipResult.base64 );
				} else {
					setUploadingImage( false );
				}
			} catch ( E ) {
				setUploadingImage( false );
			}
		}
		request();
	};
	
	/**
	 *
	 * @param base64Data
	 */
	const uploadImage = ( base64Data ) => {
		const guid = _.get( _employee, 'guid', '' );
		
		EmployeeUtil.uploadImage( base64Data, guid, {
			onSuccess: result => {
				setUploadingImage( false );
				if( _.get( result, 'status', 'error' ) !== 'error' ) {
					EmployeeValues.reloadEmployee();
				}
			},
			onError: result => {
				setUploadingImage( false );
			}
		});
	}
	
	return (
		<VStack>
			{ ( HelperSetting.isYes( settingsConstants.AD_SYNC_ENABLED ) || HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_PHOTO_EDITING ) ) && (
				<View
					style={ [ layoutStyles.containerCenter, ProfileStyles.profileAvatarContainer ] }>
					
					<EmployeeCard
						mode={ EmployeeCardModes.avatar }
						employee={ _employee }
						avatarStyle={ ProfileStyles.profileAvatar }
						avatarSize={ ThemedSizes.XL }
						avatarShowIconEro={ false }
						avatarShowIconConfidant={ false }
						avatarLoading={ uploadingImage }>
						
						<If condition={ !uploadingImage && HelperSetting.isYes( settingsConstants.PROFILE_ENABLE_PHOTO_EDITING )}>
							<ThemedPressable
								onPress={ getPermissionAsync }
								style={ [
									ProfileStyles.profileAvatar,
									ProfileStyles.profileAvatarUpload,
								]}>
								
								<View style={ ProfileStyles.profileAvatarUploadButton }>
									<Icon icon={ IconSystemConstants.CAMERA } size={ 20 } />
								</View>
							</ThemedPressable>
						</If>
					</EmployeeCard>
				</View>
			)}
			
			<ThemedBlock
				mediumPadding
				shadow>
				
				{ HelperSetting.isYes( settingsConstants.AD_SYNC_ENABLED ) && (
					<Text
						style={ ProfileStyles.adWarning }>
						
						{ i18n.t( 'page.profile.profile.ad_sync_enabled') }
					</Text>
				)}
				
				<View
					style={ ProfileStyles.profileInformation }
					testID={ TESTID.HOME.MORE.PROFILE.FORM_PROFILE_VIEW }>
					
					<Form
						values={ data }
						fields={ formFields }
						onChange={ onChange }
						hideSubmit={ true }
					/>
				</View>
			</ThemedBlock>
		</VStack>
	)
}

export default ProfileTab;
